/* eslint-disable react-refresh/only-export-components */
import { cva, type VariantProps } from "class-variance-authority"
import { Avatar as AvatarPrimitive } from "radix-ui"
import {
  Children,
  cloneElement,
  isValidElement,
  type ComponentProps,
  type ReactNode,
} from "react"
import { cn } from "cn"

const avatarVariants = cva(
  "group/avatar relative flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-neutrals-v2-200 select-none",
  {
    variants: {
      size: {
        xs: "size-7",
        sm: "size-9",
        md: "size-16",
        // 86px im Figma-Component-Set entspricht keiner Tailwind-Stufe
        lg: "size-[86px]",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  }
)

export type AvatarSize = NonNullable<
  VariantProps<typeof avatarVariants>["size"]
>

// Figma definiert „Type=Inititals" (Tippfehler, hier als „initials" normalisiert)
// nur für die Größen xs/sm — für md/lg gibt es kein Initials-Component im Set.
export type AvatarInitialsSize = Extract<AvatarSize, "xs" | "sm">

// Icon-Größe pro Avatar-Größe laut Figma-Component-Set (kein linearer Faktor).
const avatarIconSizeClassName: Record<AvatarSize, string> = {
  xs: "size-3.5",
  sm: "size-[22px]",
  md: "size-7",
  lg: "size-9",
}

type AvatarRootProps = ComponentProps<typeof AvatarPrimitive.Root>

export type AvatarImageProps = AvatarRootProps & {
  type?: "image"
  size?: AvatarSize
  src: string
  alt?: string
  /** Initialen, die angezeigt werden, solange das Bild lädt oder wenn es nicht geladen werden kann. */
  fallbackInitials?: string
}

export type AvatarIconProps = AvatarRootProps & {
  type: "icon"
  size?: AvatarSize
  /** Figma zeigt hier nur Platzhalter-Icons in wechselnder Farbe — daher freies Prop statt fixem Glyph. */
  icon: ReactNode
}

export type AvatarInitialsProps = AvatarRootProps & {
  type: "initials"
  size?: AvatarInitialsSize
  initials: string
}

export type AvatarProps =
  AvatarImageProps | AvatarIconProps | AvatarInitialsProps

function Avatar(props: AvatarProps) {
  const { className, size, ...rest } = props

  if (rest.type === "icon") {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- nur zur Diskriminierung des Props-Unions nötig
    const { type, icon, ...rootProps } = rest
    const iconSize = size ?? "sm"

    return (
      <AvatarPrimitive.Root
        data-slot="avatar"
        data-type="icon"
        data-size={iconSize}
        className={cn(avatarVariants({ size: iconSize, className }))}
        {...rootProps}
      >
        {/* Kein AvatarPrimitive.Image im Baum → Fallback wird immer gerendert. */}
        <AvatarPrimitive.Fallback
          delayMs={0}
          className="flex size-full items-center justify-center"
        >
          <span
            data-slot="avatar-icon"
            className={cn(
              "shrink-0 [&>svg]:size-full",
              avatarIconSizeClassName[iconSize]
            )}
          >
            {icon}
          </span>
        </AvatarPrimitive.Fallback>
      </AvatarPrimitive.Root>
    )
  }

  if (rest.type === "initials") {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- nur zur Diskriminierung des Props-Unions nötig
    const { type, initials, ...rootProps } = rest
    const initialsSize = size ?? "sm"

    return (
      <AvatarPrimitive.Root
        data-slot="avatar"
        data-type="initials"
        data-size={initialsSize}
        className={cn(avatarVariants({ size: initialsSize, className }))}
        {...rootProps}
      >
        <AvatarPrimitive.Fallback
          delayMs={0}
          className="text-xs leading-4 font-semibold text-labels-primary"
        >
          {initials}
        </AvatarPrimitive.Fallback>
      </AvatarPrimitive.Root>
    )
  }

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- nur zur Diskriminierung des Props-Unions nötig
    type,
    src,
    alt,
    fallbackInitials,
    ...rootProps
  } = rest
  const imageSize = size ?? "sm"

  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-type="image"
      data-size={imageSize}
      className={cn(avatarVariants({ size: imageSize, className }))}
      {...rootProps}
    >
      <AvatarPrimitive.Image
        src={src}
        alt={alt ?? ""}
        className="size-full object-cover"
      />
      {fallbackInitials && (
        <AvatarPrimitive.Fallback className="text-xs leading-4 font-semibold text-labels-primary">
          {fallbackInitials}
        </AvatarPrimitive.Fallback>
      )}
    </AvatarPrimitive.Root>
  )
}

export type AvatarGroupProps = ComponentProps<"div"> & {
  /** Wird an Avatar-Kinder ohne eigenes `size`-Prop durchgereicht. */
  size?: AvatarSize
}

function AvatarGroup({
  className,
  size,
  children,
  ...props
}: AvatarGroupProps) {
  return (
    <div
      data-slot="avatar-group"
      data-size={size}
      className={cn(
        "flex -space-x-1 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-backgrounds-tertiary",
        className
      )}
      {...props}
    >
      {size
        ? Children.map(children, (child) =>
            isValidElement<{ size?: AvatarSize }>(child) &&
            child.props.size === undefined
              ? cloneElement(child, { size })
              : child
          )
        : children}
    </div>
  )
}

export { Avatar, AvatarGroup, avatarVariants }
